import { generatePath, Link, useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { PaginationResponseType } from 'app/console-api';
import { ErrorResult, Pagination } from 'app/organizations-api/types';
import { DealListItemResponse, getMyDeals } from 'app/console-api/deals/deals';
import {
  BodyText,
  FlexColumn,
  FlexRow,
  SvgIcon,
  TableRow,
  TableRowType
} from '@make-software/cspr-design';
import Table from 'app/components/common/table/table';
import BaseTableHeader from 'app/components/common/base-table/base-table-header';
import { MyDealsListItem } from 'app/components/management/apps/deals/list/my-deals-list-item';
import { APPS_DEALS_VIEW_PATH, HOME_PATH, SIGN_IN_PATH } from 'app/route/paths';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import { HttpError } from 'app/components/utils/http-error';
import { PreloadTableWindow } from 'app/components/common/preload-table-window/preload-table-window';
import NoFound from 'assets/logos/no-found.svg';
import { StyledTableFiltersBlock, TablePageContainer } from 'app/components/common/common-styles';
import { SkeletonLoader } from 'app/components/common/skeleton-loader/skeleton-loader';
import { AppCategoriesFilter, CategoriesAdapter } from 'app/components/filters/categories-filter';
import { DealsStatusAdapter, DealsStatusFilter } from 'app/components/filters/deals-status-filter';
import { useUrlFilterController } from 'app/components/filters/url-filters';
import { DealTypesFilter } from 'app/components/filters/deal-types-filter';

export const MyDealsList = () => {
  const { appID: appID } = useParams<{ appID: string }>();
  const navigate = useNavigate();
  const filtersCtl = useUrlFilterController();
  const [loading, setLoading] = useState<boolean>(true);
  const [dealsList, setDealsList] = useState<PaginationResponseType<DealListItemResponse> | null>(
    null
  );
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);
  // TODO: Refactor pagination to cleaner way (place with/close to filters)
  const [pagination, setPagination] = useState<Pagination>({ currentPage: 1, pageSize: 5 });
  const lastFetchedFiltersKeyRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!appID) {
      return;
    }
    const filtersKey = filtersCtl.buildCacheKey([DealsStatusAdapter, CategoriesAdapter]);
    if (lastFetchedFiltersKeyRef.current !== filtersKey) {
      setPagination((p) => (p.currentPage === 1 ? p : { ...p, currentPage: 1 }));
      pagination.currentPage = 1;
    }
    lastFetchedFiltersKeyRef.current = filtersKey;

    setLoading(true);

    getMyDeals(appID, filtersCtl, pagination)
      .then((response) => {
        setDealsList(response);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to fetch deals:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [appID, pagination, filtersCtl]);

  const handlePageActions = (page: number, limit: number) => {
    if (pagination.pageSize != limit) {
      page = 1;
    }
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
      pageSize: limit
    }));
  };

  const onViewButton = (deal: DealListItemResponse) => {
    navigate(generatePath(APPS_DEALS_VIEW_PATH, { appID: appID!, id: deal.dealDetails.id }));
  };

  const noDeals = !dealsList?.data?.length && !loading && !unexpectedError;

  return (
    <TablePageContainer>
      <FlexColumn>
        <FlexRow justify={'space-between'} align={'start'} style={{ marginRight: '12px' }}>
          <StyledTableFiltersBlock>
            {appID ? (
              <>
                <DealsStatusFilter />
                <AppCategoriesFilter appID={appID} />
                <DealTypesFilter />
              </>
            ) : (
              <SkeletonLoader height={'35px'} />
            )}
          </StyledTableFiltersBlock>
        </FlexRow>
        {noDeals ? (
          <PreloadTableWindow
            icon={<SvgIcon src={NoFound} height={120} width={224} />}
            title="No deals yet"
            windowTitle=""
            subtitle={
              <>
                Go to{' '}
                <Link to={HOME_PATH}>
                  <BodyText size={1}>catalog page</BodyText>
                </Link>{' '}
                to request one
              </>
            }
          />
        ) : (
          <Table
            loading={loading}
            data={dealsList?.data || []}
            pageCount={dealsList?.page_count || 1}
            error={unexpectedError}
            pageSize={pagination.pageSize}
            currentPage={pagination.currentPage}
            handlePageActions={handlePageActions}
            tableRowType={TableRowType.TextSingleLine}
            renderDataHeader={() => (
              <TableRow>
                <BaseTableHeader fixedWidthRem={25}>Title</BaseTableHeader>
                <BaseTableHeader fixedWidthRem={15}>Details</BaseTableHeader>
                <BaseTableHeader fixedWidthRem={15}></BaseTableHeader>
                <BaseTableHeader fixedWidthRem={12}>Statistics</BaseTableHeader>
                <BaseTableHeader fixedWidthRem={8} align="right">
                  Actions
                </BaseTableHeader>
              </TableRow>
            )}
            renderPaginatedData={(one) => {
              return one.map((deal) => (
                <MyDealsListItem key={deal.dealDetails.id} deal={deal} onView={onViewButton} />
              ));
            }}
          />
        )}
      </FlexColumn>
    </TablePageContainer>
  );
};
